import { useContext } from "react";
import { Navigate } from "react-router-dom";
import { UserContext } from "../context/UserContext.jsx";

/**
 * Pass as `role` to let in anyone who holds at least one role, whichever it is. This is for the
 * admin landing page, which is the way into every role's own pages: a role added later reaches it
 * without being listed anywhere. It is not a role itself, so it can never clash with one.
 */
export const ANY_ROLE = "*";

/**
 * Renders its children only for a visitor who holds the given role, and sends everyone else to
 * the start page.
 *
 * It waits for the session to be looked up before deciding (`initialized`): the access token only
 * lives in memory, so on a fresh page load nobody has a role until the refresh call has answered.
 * Until then, and while the redirect is under way, `fallback` is shown and the children are not
 * mounted, so an admin screen never starts fetching data for a visitor who may not see it.
 *
 * The server render has no session, so it always produces `fallback`.
 *
 * This is for the user's benefit only. The API checks the role on every request.
 *
 * @param {object} props
 * @param {string|string[]} props.role a role, or a list of roles of which any one is enough.
 *   `ANY_ROLE` stands for "at least one role", alone or in a list.
 * @param {import("react").ReactNode} [props.fallback]
 * @param {import("react").ReactNode} props.children
 */
export default function RequireRole({ role, fallback = null, children }) {
  const { roles, hasRole, initialized } = useContext(UserContext);

  if (!initialized) {
    return fallback;
  }

  const allowed = (Array.isArray(role) ? role : [role]).some((candidate) =>
    candidate === ANY_ROLE ? roles.length > 0 : hasRole(candidate)
  );
  if (!allowed) {
    return (
      <>
        {fallback}
        <Navigate to="/" replace />
      </>
    );
  }

  return children;
}
